// src/api/dashboards.ts
import type { ControlStyleKey } from "@/components/dashboards/controlStyle";
import type { ActionProposal } from "@/types/actionProposals";
import { apiBlob, apiJson as clientJson, apiNoContent as clientNoContent } from "./client";
import { apiJson, apiNoContent } from "./dashboardMutations";

// Every request in this module goes through `apiJson` / `apiNoContent` from
// `dashboardMutations.ts`, which notify the mutation hook (ADR-0228) after a
// successful write. A POST that only READS (refresh, drilldown, bulk refresh,
// image lookups) uses `apiPostRead` instead: forgetting that costs one
// harmless extra request, forgetting a mutation would cost a stale Submit.

/** A POST that changes nothing on the dashboard. */
const apiPostRead = clientJson;
import type { TimeGrain } from "@/components/dashboards/TimeRangeSelector";
import type { Coverage, TimeRangePreset } from "@/utils/comparisonWindow";
import type { RowPreview } from "@verity/charts";
import type { COMPOSITION_LAYOUT } from "@verity/shared-types/composition-layout";
import type { MessageSection } from "@/types/messages";
import type { TileTablePivot } from "@/utils/tileExport";
import type { TargetComparisonBlock } from "@/types/charts";
import type { components } from "@verity/shared-types";
import type { FontRole } from "@/ui/dashboardFontVars";

// ==========================================
// TYPES
// ==========================================

export type DashboardScopeType = "account" | "workspace" | "user";
export type RefreshModeType = "manual" | "hourly" | "daily" | "weekly";

/** A block's own period (`dashboard_items.period_json`, ADR-0445): a preset
 *  in its canonical spelling (or `custom` with fixed dates) and the block's
 *  own comparison. */
export type ItemPeriod = components["schemas"]["ItemPeriod"];
export type ReportBriefDTO = components["schemas"]["ReportBriefDTO"];
export type ReportBriefWrite = components["schemas"]["ReportBriefWrite"];

/** A block a write moved on the server (ADR-0513). */
export type LayoutMove = { id: string; layout_x: number; layout_y: number; layout_w: number; layout_h: number };

export type DashboardItemDTO = {
  id: string; // ditem_...
  /** The block's identity across draft, published row and copy; an author's
   *  interaction targets name tiles by it (plan 2026-10-05 B5). Optional in
   *  this hand-written type only so older fixtures compile. */
  block_key?: string;
  name: string | null;
  /** Ids of the dashboard's saved filters this block applies (ADR-0232),
   *  on top of the dashboard's and the tab's. Optional in this hand-written
   *  type only so older fixtures compile; the server always sends it. */
  filter_refs?: string[];
  chart_spec_json: Record<string, unknown>;
  chart_config_json: Record<string, unknown> | null;
  data_json: Record<string, unknown> | null;
  query_sql: string | null;
  query_ir_json: Record<string, unknown> | null;
  data_source_id: string | null;
  /** The account-level mart this block reads, for a block bound to NO data
   *  source. Derived server-side from the block's own query, never stored.
   *  `null` for a per-source block and for one that reads no such mart.
   *  Optional in this hand-written type only so older fixtures compile. */
  aggregate_connector_type?: string | null;
  /** Grid units. Whole numbers on a grid dashboard; on a free one up to two
   *  decimals (plan 2026-10-11 free block layout, D1). */
  layout_x: number;
  layout_y: number;
  layout_w: number;
  layout_h: number;
  /** Stacking order within the tab's blocks on a free dashboard, higher on
   *  top (D2). Optional so older fixtures compile; the server sends it. */
  layout_z?: number;
  /** Set only on the answer of a write that moved OTHER blocks on the
   *  server: a KPI on a page grown to what it holds pushed them down
   *  (ADR-0513). The editor adopts them in the same state update as this
   *  item (`adoptLayoutMoves`), so its grid has nothing to PATCH back. */
  layout_moves?: LayoutMove[] | null;
  conversation_id: string | null;
  refresh_mode: RefreshModeType;
  last_refreshed_at: string | null;
  refresh_status: string | null; // null=idle, "running", "failed"
  refresh_started_at: string | null;
  last_refresh_error: string | null;
  /** The block's own period; null = it follows the dashboard (ADR-0445).
   *  A block with one is reached by neither the header nor a date range
   *  control (decision 5). Optional in this hand-written type only so older
   *  fixtures compile; the dashboard GET always sends it. */
  period?: ItemPeriod | null;
  /** Client-only, never sent by the server: a refresh THIS page asked for the
   *  current view (range, filters) failed, so the numbers still in the item
   *  belong to another view and must not be drawn under this one (browser
   *  gate 2026-10-06). A scheduled refresh's failure (`refresh_status` from
   *  the server) does not set it: the last good data stays on screen then. */
  view_refresh_failed?: boolean;
  cached_data_json: Record<string, unknown> | null; // Deprecated: use data_json
  // Effective date range of cached_data_json. Updated by refresh, NOT by
  // creation. Distinct from query_ir_json.start_date/end_date (frozen IR).
  // Hydration uses this for cache-key matching so the in-memory cache
  // actually hits when the user opens the page with the same range that
  // was last refreshed. Shape: {start_date, end_date, timezone, time_grain}.
  cached_date_range: {
    start_date: string;
    end_date: string;
    timezone: string;
    time_grain: string | null;
  } | null;
  // The last day this tile's source delivered: MAX(day) over the page's wide
  // stores of tiles on the same (source, dataset), ISO date. A delivery day,
  // NOT capped at a window, so it holds for any period the tile shows. Set by
  // the dashboard GET only — a refresh response does not carry it, so the
  // page keeps the GET's value. Feeds the tile's empty message (plan
  // 2026-10-11, E1). Optional: absent on an older backend or with no rows.
  source_through_date?: string | null;
  // When the browser must stop serving what this GET hydrated for the tile:
  // the moment its customer source's rule (refresh interval, change check, a
  // Refresh data press) turns it stale. Set on customer-source tiles only;
  // null/absent on managed and DS-less tiles (the managed rule). A customer
  // tile without it is not hydrated (plan 2026-10-21 refresh interval, D7).
  cache_valid_until?: string | null;
  // Comparison data inline in dashboard GET response — present only when
  // caller passes ?comparison_mode=previous_year|previous_period AND the
  // item is wide-cacheable on a managed dataset. Shape matches the
  // ComparisonData type below: {columns, rows, row_count, date_range}.
  // See docs/plans-pending/2026-05-18-comparison-data-in-dashboard-get-response.md.
  comparison_cached_data_json: Record<string, unknown> | null;
  // The target leg BESIDE a window comparison (W1.8; ADR-0267): present only
  // when the GET asked `with_target=true` next to a window comparison_mode.
  // A ComparisonData with its `target` block. Optional on the wire: a backend
  // that predates it sends nothing, and the tile then draws the window leg
  // alone.
  target_cached_data_json?: Record<string, unknown> | null;
  // Pre-warmed cache snapshots for common quick-select presets
  // (yesterday → last_month). Shape:
  //   { "<preset>": {start_date, end_date, time_grain,
  //                  columns, rows, row_count, row_count_truncated} }
  // The frontend hydrate writes one mainCache entry per preset so
  // date-switches to those presets are instant cache-hits. Generated
  // by the backend refill helper from the wide cache (zero BQ cost).
  prewarmed_ranges_json: Record<string, PrewarmedRangeData> | null;
  // Set by the backend on tiles whose dimension can be broken down one level
  // further (today: the Ecom "Marketing Channel Performance" table →
  // campaign). Absent/null on every other tile, which is what keeps the
  // expand affordance off them. Drillability is a fact of the dataset family,
  // not tile configuration — see the drilldown-edge registry.
  drilldown?: TileDrilldownDTO | null;
  /** The control dimensions this DATA tile is steered by (plan 2026-09-21
   *  control block, S5): a tab filter on one of these reaches this tile, and
   *  a tile without it never gets that value (`utils/tabFilters.ts`). Empty
   *  when none. Optional in this hand-written type only so older fixtures
   *  compile; the server always sends it, and absent reads as empty. */
  control_dimensions?: string[];
  /** The open viewer filters that reach this tile (plan 2026-10-05 dashboard
   *  interactivity, A2): the dimensions its dataset carries that a viewer may
   *  filter on, plus the connector discriminators. A bar filter on a key this
   *  list lacks is not sent for this tile, which keeps it on its cache.
   *  Empty on a raw-SQL tile. Optional because older payloads lack it; absent
   *  means "unknown", and `utils/tabFilters.ts` then keeps the old rule. */
  filterable_dimensions?: FilterableDimensionDTO[];
  /** A CONTROL tile's dropdown as this viewer's account sees it (options,
   *  their availability, the account's default). Null on every other tile,
   *  and on a control whose dimension the backend no longer knows. */
  control?: DashboardControlDTO | null;
  /** The line under a chart or table block's title (plan 2026-10-16-a-block-
   *  subtitle): the author's template, at most 200 characters, one paragraph,
   *  possibly with `{{name}}` tokens. Null when the block has none. Optional
   *  in this hand-written type only so older fixtures compile. */
  subtitle?: string | null;
  /** The stamped smart-text declaration of the subtitle's tokens
   *  (`{language, variables, compare, columns, metric_formats}`); null for a
   *  plain-text subtitle. Read through `smartText/servedSentence.ts`. */
  subtitle_smart_text?: Record<string, unknown> | null;
  /** The block's own tile frame (plan 2026-10-16-tile-frame-style-settings);
   *  null = follow the dashboard's `tile_frame`. Optional so older fixtures
   *  compile. Read through `tileFrame.ts` `resolveTileFrame`. */
  tile_title_rule?: boolean | null;
  tile_corners?: TileCornersDTO | null;
  tile_outline?: boolean | null;
  /** The subtitle's width (plan 2026-10-16-a-subtitle-may-keep-a-reading-
   *  width); null = follow the dashboard. */
  tile_subtitle_width?: TileSubtitleWidthDTO | null;
  /** The block's OWN text style per role (plan 2026-10-17-dashboard-text-
   *  style-complete): only what it sets; the dashboard's `text_styles` fill
   *  the rest. Read through `@/ui/dashboardFontVars`, which re-checks every
   *  value. Optional so older fixtures compile. */
  text_styles?: TextStylesDTO;
  created_at: string;
};

export type TileCornersDTO = "rounded" | "square";
export type TileSubtitleWidthDTO = "full" | "narrow";
export type TileFrameSourceLevel = "dashboard" | "account" | "platform";
/** The attributes of one role's text style (plan 2026-10-17-dashboard-text-
 *  style-complete, D5): font (catalogue key), color (`text` | `muted` |
 *  `palette_N` | `#rrggbb`), size (step in %), weight, italic, underline, and
 *  on some roles align, letter_case, line_spacing. Null/absent = the level
 *  above. The role list lives in `@/ui/dashboardFontVars`. */
export type TextRoleStyle = {
  font?: string | null;
  color?: string | null;
  size?: number | null;
  weight?: 400 | 500 | 600 | 700 | null;
  italic?: boolean | null;
  underline?: boolean | null;
  strikethrough?: boolean | null;
  align?: "left" | "center" | "right" | null;
  letter_case?: "none" | "upper" | null;
  line_spacing?: "compact" | "normal" | "relaxed" | null;
};
export type TextStyleAttribute = keyof TextRoleStyle;
export type TextStylesDTO = Partial<Record<FontRole, TextRoleStyle>>;
export type TextStylesSourceDTO = Partial<Record<FontRole, Partial<Record<TextStyleAttribute, TileFrameSourceLevel>>>>;
/** A text style write: per role, PRESENCE of an attribute decides; null
 *  follows the level above. */
export type TextStylesWrite = { text_styles: Partial<Record<FontRole, TextRoleStyle>> };

/** A dashboard's EFFECTIVE tile frame, resolved by the server. */
export type TileFrameDTO = {
  title_rule: boolean;
  corners: TileCornersDTO;
  outline: boolean;
  /** Optional: an older backend does not send it (reads as "full"). */
  subtitle_width?: TileSubtitleWidthDTO;
};

/** The account's dashboard colours (plan 2026-10-16-account-dashboard-
 *  appearance): the preset name and its page and block colour, lowercase
 *  `#rrggbb`, resolved by the server. Read through `dashboardSurfaceStyle`,
 *  which re-checks both hexes. */
export type DashboardSurfaceDTO = { preset: string; page_bg: string; block_bg: string };
export type TileFrameSourceDTO = {
  title_rule: TileFrameSourceLevel;
  corners: TileFrameSourceLevel;
  outline: TileFrameSourceLevel;
  subtitle_width?: TileFrameSourceLevel;
};

/** A control block's dropdown (plan 2026-09-21, S5), straight from the
 *  OpenAPI spec. */
export type DashboardControlDTO = components["schemas"]["DashboardControlDTO"];
export type FilterableDimensionDTO = components["schemas"]["FilterableDimensionDTO"];
export type DashboardControlOptionDTO = components["schemas"]["DashboardControlOptionDTO"];

export type TileDrilldownDTO = {
  /** Dimension the children are grouped by, e.g. "campaign". */
  breakdown_key: string;
  /** Singular, customer-facing label for that dimension, e.g. "Campaign". */
  breakdown_label: string;
  /** How many children the endpoint returns before folding the rest into the
   *  remainder row. */
  limit: number;
  /** What a viewer may break a row out by instead, the default first (at
   *  most 15). Empty when the caller may not query the tile. */
  options?: Array<{ key: string; label: string }>;
};

export type PrewarmedRangeData = {
  start_date: string;
  end_date: string;
  time_grain: TimeGrain | null;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  row_count: number;
  row_count_truncated: boolean;
  // The two shifted comparison snapshots — the backend writes both for every
  // preset (`dashboard_refresh_service` prewarm entry), so hydrate writes
  // mainCache + one compCache entry per leg and refreshAllItems' Phase 1
  // cache-check passes under either window mode. Until plan
  // 2026-09-14-the-plan-leg-on-every-surface (S3) only the previous-year
  // leg was read, so a date switch under "Previous period" always cost a
  // roundtrip on a warmed preset.
  comparison_previous_year?: ComparisonData;
  comparison_previous_period?: ComparisonData;
  // The chart-spec fields the response chokepoint owns, rendered
  // server-side for THIS preset's rows (backend
  // `_preset_chart_spec_fields`). `rows` above stays RAW long-form; for
  // the breakdown / funnel / metric-bar families the chokepoint pivots
  // them and rewrites `y_keys`, so a client that pastes the raw rows over
  // the item-level spec gets dataKeys no row has and renders an empty
  // chart under a full legend.
  //   set:  spec keys to apply over the item-level spec (always rows +
  //         data, plus y_keys / y_keys_label / series_config when the
  //         chokepoint produced them).
  //   drop: spec keys the chokepoint REMOVED (today only series_config) —
  //         named explicitly because the client spreads the item-level
  //         spec first.
  // Written for EVERY entry, pivot family or not, so hydration gates on
  // key presence instead of re-deriving the pivot trigger. Optional: an
  // entry written before this shipped simply has no `pivot`, and
  // hydration skips it rather than writing the empty-rendering shape.
  //
  // `raw_rows` is NOT in here by design — it is the long-form row list, so
  // carrying it would write `rows` twice into the same JSONB blob. The
  // client sources it from `rows` above instead (hydratePrewarmedRanges);
  // it must not inherit the item-level spec's, which holds the item's own
  // default range.
  pivot?: { set: Record<string, unknown>; drop: string[] };
};

export type DashboardTabDTO = {
  id: string; // dtab_...
  name: string;
  sort_order: number;
  /** The package a new block on this tab starts on (sp_... public id), or
   *  null: the unit a person chooses (plan 2026-09-03 account-level data
   *  products, P3). A suggestion for the editor only. */
  default_package_id?: string | null;
  default_package_label?: string | null;
  /** The source instance of that package (ds_... public id), or null: set
   *  when the pack is shown per source and the tab picked one of them. */
  default_data_source_id?: string | null;
  items: DashboardItemDTO[];
  /** The tab's background shapes, bottom first (plan 2026-10-09 dashboard
   *  shapes, D1), already re-parsed by the server. Optional so older
   *  fixtures compile; the server always sends it. Untrusted all the same:
   *  every reader goes through `coerceDashboardBackdrop`. */
  backdrop?: components["schemas"]["DashboardBackdropDTO"]["shapes"];
};

/** One scope filter (ADR-0230): a dashboard's or a tab's filter in the block
 *  filter's own shape. `op` is one of `=`, `!=`, `IN`, `NOT IN`, `>`, `>=`,
 *  `<`, `<=`; `value` a string, a number, a boolean (a yes/no dimension), or
 *  a list of them for the two list operators. */
export type ScopeFilterDTO = { key: string; op: string; value: unknown };

/** A saved filter (ADR-0232): named once on the dashboard, referenced by id
 *  from the dashboard, a tab (topic) and a block through `filter_refs`. */
export type FilterCatalogEntryDTO = { id: string; name: string; filters: ScopeFilterDTO[] };

/** A scope's two lists (ADR-0232): each replaced whole when present, left as
 *  it is when absent. `[]` clears. */
export type ScopeFiltersBody = { filters?: ScopeFilterDTO[]; filter_refs?: string[] };

/** A dimension a scope filter may name, with what it would cover
 *  (`GET /dashboards/{id}/filter-fields`). */
export type ScopeFilterFieldDTO = {
  key: string;
  label: string;
  blocks_covered: number;
  blocks_in_scope: number;
  datasets: string[];
  /** The value type every covering block agrees on; null when they disagree
   *  or a covering block has no known type. A `boolean` field takes true or
   *  false. */
  value_type?: "string" | "number" | "boolean" | "date" | null;
};

export type DashboardTopicDTO = {
  id: string; // dtop_...
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  /** How the topic shows its tabs: a dropdown under its name ("menu", the
   *  default) or a strip on the page ("strip"). Optional here so older
   *  fixtures compile; the server always sends it. */
  tab_style?: "menu" | "strip";
  tabs: DashboardTabDTO[];
  /** The tab's scope filters (ADR-0230), applied to every block on it after
   *  the dashboard's. Optional in this hand-written type only so older
   *  fixtures compile; the server always sends it. */
  filters?: ScopeFilterDTO[];
  /** Ids of the dashboard's saved filters this tab applies (ADR-0232). */
  filter_refs?: string[];
  /** The page's filter bar choice (plan 2026-10-06 interaction settings, A):
   *  true / false, or null to follow the dashboard's `show_filter_bar`.
   *  Optional so older fixtures compile; the server always sends it. */
  show_filter_bar?: boolean | null;
  /** A bounded page's format (ADR-0496 D2): the page is a fixed grid of
   *  that format's rows; null is an endless tab. Optional so older fixtures
   *  compile; the server always sends it. */
  page_format?: PageFormatKey | null;
};

/** The keys of the server's `PAGE_FORMATS`, from the emitted layout spec. */
export type PageFormatKey = keyof typeof COMPOSITION_LAYOUT.page_formats;

/**
 * Denormalized DataSource info attached to per-DS managed dashboards.
 *
 * - `public_id` / `name`: the bound DataSource (no internal UUIDs leaked).
 * - `last_successful_run_at`: "marts are queryable" gate. Per-source
 *   connectors read the linked MPC; per-DS-shaped account-level
 *   connectors (Brevo) are resolved from the account-level transform history —
 *   see _resolve_account_level_run_state in dashboard_routes.py. Null when
 *   this source's data has never been transformed successfully.
 * - `last_run_status` / `last_error_message`: surface "first run failed"
 *   state distinct from "waiting for first run". Branch ONLY on 'FAILED' —
 *   account-level connectors also emit 'STARTED'/'CANCELLED' here.
 *
 * `null` for account-aggregate managed dashboards and user/workspace
 * dashboards (those don't bind to a single DS).
 */
export type DashboardDataSourceMiniDTO = {
  public_id: string;
  name: string;
  // ownership_mode drives client-side cache TTL semantics in
  // `utils/dashboardCache.ts` hydration: "managed" | "transform" |
  // "semantics" → 30-min TTL; "customer" → 1-min TTL.
  // See docs/plans-pending/2026-05-18-hydrate-dashboard-cache-from-get-response.md.
  // Optional because old backends may not include it (graceful default).
  ownership_mode: string | null;
  last_successful_run_at: string | null;
  last_run_status: string | null;
  // The same value as DashboardDTO.data_through_date (the last day this
  // dashboard's tiles can draw), kept here for readers of the per-source
  // field. Only set by the dashboard detail GET.
  data_through_date?: string | null;
  last_error_message: string | null;
  // How long this source's tiles stay cached before the warehouse is read
  // again, in hours: set on a customer (direct-connect) source only. The
  // "Refresh data" entry names it and links to the source that owns it.
  // Optional: an older backend answers without it.
  cache_refresh_hours?: number | null;
};

/** What a `dashboards` row is (ADR-0496 D1): fixed at creation. */
export type DashboardKind = "dashboard" | "report";

export type DashboardSummaryDTO = {
  id: string; // dash_...
  kind?: DashboardKind;
  /** The period a viewer without a pick of their own opens on (ADR-0496 D3);
   *  null or absent: none. */
  default_period?: ItemPeriod | null;
  name: string;
  description: string | null;
  icon: string | null;
  scope_type: DashboardScopeType;
  workspace_id: string | null;
  topic_count: number;
  item_count: number;
  is_default: boolean;
  tags: string[];
  is_managed: boolean;
  link_url: string | null;
  updated_at: string;
  // Which connector this dashboard belongs to, e.g. "SHOPIFY", "GA4_BQ_EXPORT".
  // Derived server-side (aggregate_connector_type, else the bound source's
  // pipeline connector) — the same key <ConnectorIcon type={…}> takes on the
  // data-source screens. Null for customer dashboards and for the cross-source
  // composite, which has no single connector. Optional so an older backend
  // simply falls through to the stored `icon` in DashboardIcon.
  connector_type?: string | null;
  data_source: DashboardDataSourceMiniDTO | null;
  // Review / publishing state (composition builder, plan 2026-09-02). A
  // personal draft carries its status and target; a published copy carries
  // its version number. Every managed dashboard is APPROVED.
  review_status: DashboardReviewStatus;
  revision: number;
  publish_target_scope: PublishTargetScope | null;
  has_published_copy: boolean;
  published_version_number: number | null;
  // ADR-0228: published as a definition (non-managed, non-personal), not
  // only as a stamp. Optional: an older backend answers without it.
  is_published_row?: boolean;
  // The CALLER has hidden this Verity dashboard for themselves (plan
  // 2026-10-06-dashboards-overview-add-tile-list-view-hide-managed). Only
  // ever true on a managed row. A view preference, never access control.
  // Optional: an older backend answers without it.
  hidden?: boolean;
  /** "granted": the caller reaches this row only through a read right of
   *  their own (ADR-0506); the lists file it under "Shared with you". */
  via?: "granted" | null;
};

export type DashboardReviewStatus = "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "REJECTED";
export type PublishTargetScope = "workspace" | "account";

// Materialized page payload for one (tab, warmed preset) — page-level cache
// (docs/plans-pending/2026-06-30-managed-dashboard-page-level-cache.md).
// Items absent from covered_item_ids must be fetched per-tile (hybrid render).
export type TabSnapshotItemPayload = {
  chart_spec_json: Record<string, unknown>;
  chart_config_json: Record<string, unknown> | null;
  cached_data_json: {
    columns: string[];
    rows: Record<string, unknown>[];
    row_count: number;
    row_count_truncated: boolean;
  };
  row_count: number;
  row_count_truncated: boolean;
  comparison_previous_year: ComparisonData | null;
  comparison_previous_period: ComparisonData | null;
};

export type TabSnapshotDTO = {
  tab_id: string; // dtab_...
  preset: string;
  start_date: string;
  end_date: string;
  time_grain: string | null;
  covered_item_ids: string[];
  items: Record<string, TabSnapshotItemPayload>;
  refreshed_at: string;
};

// ── Aggregate (NULL-DS) dashboard surface (W2.6/2.7) ──────────────────────
// Populated only for account-level aggregate dashboards (Meta first);
// null/empty for a normal per-DS dashboard. Connector-generic: Google Ads
// later declares customer_id here with no frontend type change.

export type FilterOptionValueDTO = {
  value: string; // canonical dimension value, e.g. "act_1234567890"
  label: string; // human label (account name, else the value)
};

export type AggregateFilterOptionDTO = {
  dimension: string; // e.g. "ad_account_id"
  label: string; // e.g. "Ad account"
  options: FilterOptionValueDTO[];
};

// Readiness of the account-level marts that back an aggregate dashboard.
// Replaces the per-DS `data_source.last_run_status` banner (absent for NULL-DS).
export type AggregateReadinessDTO = {
  // "ready" | "provisioning" (waiting for first build) | "error" (last build failed)
  state: "ready" | "provisioning" | "error";
  last_built_at: string | null; // ISO of last successful build
};

// A viewer's / admin's dimension filter selection, e.g.
// { ad_account_id: ["act_1", "act_2"] }. Empty/absent = all values.
export type DimensionFilters = Record<string, string[]>;

export type DashboardDTO = {
  id: string; // dash_...
  /** Absent on an older response: a dashboard. */
  kind?: DashboardKind;
  /** The period a viewer without a pick of their own opens on (ADR-0496 D3);
   *  null or absent: none. */
  default_period?: ItemPeriod | null;
  /** Set only on the answer of a default-period write that regrew the KPIs
   *  on the report's pages (ADR-0513, plan step 7): each grown card and each
   *  block it pushed. The editor adopts them (`applyLayoutMoves`) and never
   *  keeps the list in state. */
  layout_moves?: LayoutMove[] | null;
  /** A report's brief (plan 2026-10-22 step B3); null on a dashboard. */
  report_brief?: ReportBriefDTO | null;
  /** A report's bound lens (public id), and the workspace whose lenses it
   *  may bind (null = account lenses only). Null on a dashboard. */
  lens_id?: string | null;
  lens_workspace_id?: string | null;
  name: string;
  description: string | null;
  icon: string | null;
  scope_type: DashboardScopeType;
  workspace_id: string | null;
  is_default: boolean;
  tags: string[];
  is_managed: boolean;
  link_url: string | null;
  hero_item_id: string | null;
  topics: DashboardTopicDTO[];
  /** The dashboard's scope filters (ADR-0230); see DashboardTopicDTO.filters. */
  filters?: ScopeFilterDTO[];
  /** The saved filters (ADR-0232) and the ids the dashboard itself applies. */
  filter_catalog?: FilterCatalogEntryDTO[];
  filter_refs?: string[];
  /** Moves on every scope-filter write: a level's rows or references, a
   *  block's references, a saved filter's clauses, a tree write. */
  filters_revision?: number;
  created_at: string;
  updated_at: string;
  data_source: DashboardDataSourceMiniDTO | null;
  // Fresh snapshot of the first tab for the resolved default preset (or
  // null). Hydrated into the client cache on load — see
  // utils/dashboardCache.ts hydrateFromTabSnapshot.
  tab_snapshot?: TabSnapshotDTO | null;
  // Aggregate-dashboard surface (W2.6/2.7). null/empty for per-DS dashboards.
  aggregate_connector_type?: string | null;
  // Can this dashboard ever have a (tab, preset) page snapshot? False for
  // dashboards without a data source (account-aggregate, composite) — the
  // refill is DS-keyed, so /tabs/:tabId/page can only ever 404 for them.
  // Server-computed on purpose: a client-side copy of the rule would keep
  // suppressing the request on the day those snapshots do get built.
  page_snapshot_eligible?: boolean;
  // Presets the backend materializes snapshots for (server-owned; the
  // frontend used to keep its own drifting copy).
  warmed_presets?: string[];
  filter_options?: AggregateFilterOptionDTO[];
  readiness?: AggregateReadinessDTO | null;
  // The last day THIS dashboard's tiles can draw (ISO date): MAX(day) over
  // its items' wide day-row store, for every dashboard shape — per-source and
  // account-aggregate alike (ADR-0266). Drives the header clock and the
  // "Charts show data through …" banner. Optional: an older backend answers
  // without it (the header then falls back to data_source.data_through_date).
  data_through_date?: string | null;
  // The server's verdict on that date (plan 2026-09-14-source-freshness, F3d):
  // scored by the same function as every source badge. The header clock and
  // the banner colour read it; absent on an older backend → local fallback.
  freshness?: {
    state: "fresh" | "behind" | "stale" | "unknown";
    days_behind?: number | null;
    data_through?: string | null;
    reason?: string | null;
    warn_after_days?: number;
    fail_after_days?: number;
  } | null;
  default_dimension_filters?: DimensionFilters | null;
  // Review / publishing (composition builder). The server decides what the
  // caller may do; the page renders buttons from `permissions` and guesses
  // nothing itself (plan 2026-09-02, B4).
  review_status: DashboardReviewStatus;
  revision: number;
  publish_target_scope: PublishTargetScope | null;
  has_published_copy: boolean;
  published_version_number: number | null;
  // ADR-0228: published as a definition (non-managed, non-personal), not
  // only as a stamp. Optional: an older backend answers without it.
  is_published_row?: boolean;
  /** The editing mode (plan 2026-10-05-dashboard-version-history, B1):
   *  off = live, viewers see every change; on = viewers see the published
   *  row only. A shared draft answers with its published row's values.
   *  Optional: an older backend answers without them. */
  publishing_enabled?: boolean;
  require_review?: boolean;
  rejection_reason: string | null;
  /** The filter bar's default for every page (plan 2026-10-06 interaction
   *  settings, A); a page may override it. Optional so older fixtures
   *  compile; the server always sends it (default false). */
  show_filter_bar?: boolean;
  /** A click on a value asks before it filters (B). Default false: the
   *  click filters at once and a toast offers Undo. */
  confirm_click_filter?: boolean;
  /** How blocks are laid out (plan 2026-10-11 free block layout, D2):
   *  `grid` snaps them to whole cells and closes gaps; `free` places them to
   *  0.01 grid unit and lets them overlap. Optional so older fixtures
   *  compile; absent means grid. */
  layout_mode?: LayoutMode;
  /** The 8 effective theme colours the drawing slots `palette_1..8` resolve
   *  to (ADR-0380), lowercase `#rrggbb`, resolved by the server: the
   *  dashboard's own ("dashboard") or its account palette ("account").
   *  Optional so older fixtures compile; the server always sends them.
   *  Read through `dashboardTheme()`, which re-checks every value. */
  theme_colors?: string[];
  theme_colors_source?: "dashboard" | "account";
  /** The dashboard's series pins (ADR-0476): `m:<metric>` / `d:<value>` →
   *  `palette_N` or `#rrggbb`, held by every chart on the dashboard unless a
   *  chart overrides it. `{}` = none. Optional so older fixtures compile; the
   *  server always sends it. Untrusted: read through `readSeriesPins`. */
  series_colors?: Record<string, string>;
  /** The effective tile frame and where each field comes from (plan
   *  2026-10-16-tile-frame-style-settings). Optional so older fixtures
   *  compile; the server always sends them. Read through `TileFrameProvider`. */
  tile_frame?: TileFrameDTO;
  tile_frame_source?: TileFrameSourceDTO;
  /** What the dashboard follows where it sets nothing (the account over
   *  Verity's default): the editor's "default" a dashboard value overrides. */
  tile_frame_inherited?: TileFrameDTO;
  /** The effective text style per role and attribute, where each comes from,
   *  and what the dashboard inherits from the account (plan 2026-10-17-
   *  dashboard-text-style-complete). Optional so older fixtures compile; the
   *  server always sends them. */
  text_styles?: TextStylesDTO;
  text_styles_source?: TextStylesSourceDTO;
  text_styles_inherited?: TextStylesDTO;
  /** The dashboard's own control style (plan 2026-10-19-control-block-
   *  styling): only what it sets, null = each control's own look. A block's
   *  own keys (its `control`) win. Read through `coerceControlStyle`. */
  control_style?: Partial<Record<ControlStyleKey, string | number | null>>;
  /** The account's dashboard colours. Optional so older fixtures (and an
   *  older cached DTO) compile; the server always sends them. */
  surface?: DashboardSurfaceDTO;
  permissions?: DashboardPermissions;
  /** Public id of the published copy (on a draft) or of the draft (on a
   *  published copy), for "View published" / "Edit draft". */
  published_dashboard_id?: string | null;
  source_draft_id?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  reviewed_by_name?: string | null;
};

export type DashboardPermissions = {
  edit: boolean;
  submit_for_review: boolean;
  approve: boolean;
  reject: boolean;
  revert_to_draft: boolean;
  unpublish: boolean;
  restore_version: boolean;
  /** Version history (plan 2026-10-05-dashboard-version-history): every kind
   *  of version, and naming one. Optional: an older backend reads as "no". */
  view_history?: boolean;
  save_version?: boolean;
  copy: boolean;
  /** Copy into the other kind: the target kind's capability (B3b).
   *  Optional: an older backend reads as "no". */
  copy_as?: boolean;
  adopt: boolean;
  delete: boolean;
  /** Set a table column's unit/decimals (plan 2026-09-03). Optional so an
   *  older backend reads as "not allowed", never as "allowed". */
  format_columns?: boolean;
  /** Add colour rules to a table (ADR-0351): `format_columns`'s rule on a
   *  self-built dashboard, never on a managed one. Optional: absent reads as
   *  "not allowed". */
  format_conditions?: boolean;
  /** The two editing modes (plan 2026-10-05-dashboard-version-history,
   *  B3/B4). Optional: absent reads as "not allowed". On a shared draft the
   *  two `set_*` bits are false by construction (the settings belong to the
   *  published row); read them from the published row. */
  publish?: boolean;
  set_publishing?: boolean;
  set_require_review?: boolean;
  share?: boolean;
  /** Read rights (ADR-0506): give or take people's read access (account
   *  admins), and ask an admin for it. Optional: absent reads as "no". */
  grant_read?: boolean;
  request_read?: boolean;
};

export type DashboardReviewAction =
  | "submitted"
  | "approved"
  | "rejected"
  | "reverted_to_draft"
  | "unpublished"
  | "restored_version";

export type DashboardReviewEventDTO = {
  action: DashboardReviewAction;
  actor_name: string;
  from_status: string;
  to_status: string;
  note: string | null;
  created_at: string;
};

export type DashboardTransitionResponse = {
  dashboard: DashboardSummaryDTO;
  event: DashboardReviewEventDTO;
  published_dashboard_id: string | null;
};

export type DashboardVersionDTO = {
  version_number: number;
  approved_at: string;
  approved_by_name: string;
  note: string | null;
};

export type DashboardReviewQueueItemDTO = {
  id: string;
  name: string;
  author_name: string;
  submitted_at: string | null;
  publish_target_scope: PublishTargetScope | null;
  target_workspace_id: string | null;
  target_workspace_name: string | null;
};

// ==========================================
// DASHBOARD API
// ==========================================

export async function listDashboards(params?: {
  scope_type?: DashboardScopeType;
  workspace_id?: string;
  /** Which kind the list shows (ADR-0496 D1). Omitted: dashboards, the
   *  overview's list; `report` is the Reports list. */
  kind?: DashboardKind;
}): Promise<DashboardSummaryDTO[]> {
  const q = new URLSearchParams();
  if (params?.scope_type) q.set("scope_type", params.scope_type);
  if (params?.workspace_id) q.set("workspace_id", params.workspace_id);
  if (params?.kind) q.set("kind", params.kind);

  const queryString = q.toString();
  return apiJson<DashboardSummaryDTO[]>(
    `/dashboards${queryString ? `?${queryString}` : ""}`
  );
}

export async function getDashboard(
  dashboardId: string,
  opts?: {
    /** The comparison window. The account's plan is never the comparison on
     *  a dashboard; it rides beside a window as `with_target` (plan
     *  2026-09-15-the-target-is-an-extra-comparison-not-a-mode). */
    comparison_mode?: "previous_year" | "previous_period";
    /** The plan as a second leg beside a window mode (W1.8): the item DTO then
     *  carries `target_cached_data_json` next to the window's leg. */
    with_target?: boolean;
    /** The selected preset. Decides whether the backend derives the
     *  previous-year window as a 364-day shift or a calendar year — it must
     *  reach the same answer this client does, because that window is part of
     *  the comparison cache key. Omitted is safe: the backend then uses
     *  calendar alignment and the cache simply misses. */
    preset?: TimeRangePreset;
  }
): Promise<DashboardDTO> {
  const params = new URLSearchParams();
  if (opts?.comparison_mode) {
    params.set("comparison_mode", opts.comparison_mode);
  }
  if (opts?.with_target && opts.comparison_mode) {
    params.set("with_target", "true");
  }
  if (opts?.preset) {
    params.set("preset", opts.preset);
  }
  const qs = params.toString();
  const suffix = qs ? `?${qs}` : "";
  return apiJson<DashboardDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}${suffix}`
  );
}

/**
 * Fetch the materialized (tab, preset) page snapshot — one cheap read
 * replacing the per-tab bulk-refresh fan-out. Throws on 404 (no fresh
 * snapshot); callers catch and fall back to the per-tile refresh path.
 */
export async function getTabPage(
  dashboardId: string,
  tabId: string,
  preset: string
): Promise<TabSnapshotDTO> {
  const params = new URLSearchParams({ preset });
  return apiJson<TabSnapshotDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/page?${params}`
  );
}

/**
 * Every new dashboard is a personal draft of the caller. The publish target
 * says where it goes once submitted and approved; leave it out for a
 * dashboard that stays personal (composition builder B1).
 */
export async function createDashboard(payload: {
  name: string;
  description?: string;
  tags?: string[];
  publish_target_scope?: PublishTargetScope;
  publish_target_workspace_id?: string;
}): Promise<DashboardDTO> {
  return apiJson<DashboardDTO>(`/dashboards`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export type DashboardCopyResponse = {
  dashboard: DashboardDTO;
  /** Tiles left out because the copier may not query their source. */
  skipped_items: number;
  skipped_item_names: string[];
  /** Copy as dashboard: the blocks only a report takes, left out. */
  dropped_report_only_items?: number;
};

/**
 * Copy a dashboard (managed, published or draft) into a personal draft of
 * the caller. `adopt` re-attaches a published dashboard whose draft is gone
 * (reviewers only). Composition builder B2.
 */
export async function copyDashboard(
  dashboardId: string,
  payload: {
    name?: string;
    adopt?: boolean;
    /** Copy into the other kind (plan 2026-10-22 step B3b): "report" on a
     *  dashboard, "dashboard" on a report. */
    kind?: DashboardKind;
    /** Copy as report: the format of pages that are not pages already. */
    page_format?: PageFormatKey;
    /** Copy as report: the workspace (public id) it is made in; default the
     *  source's own. */
    workspace_id?: string;
  } = {}
): Promise<DashboardCopyResponse> {
  return apiJson<DashboardCopyResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/copy`,
    { method: "POST", body: JSON.stringify(payload) }
  );
}

// ---- Review transitions (composition builder). Every transition carries the
// revision the caller saw as If-Match; the server answers 409 when the row
// moved since, and the page then offers a reload instead of overwriting.

function ifMatch(revision: number): Record<string, string> {
  return { "If-Match": String(revision) };
}

export async function submitDashboardForReview(
  dashboardId: string,
  revision: number
): Promise<DashboardTransitionResponse> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/submit-for-review`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: ifMatch(revision),
  });
}

export async function approveDashboard(
  dashboardId: string,
  revision: number,
  body: { note?: string } = {}
): Promise<DashboardTransitionResponse> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/approve`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: ifMatch(revision),
  });
}

export async function rejectDashboard(
  dashboardId: string,
  revision: number,
  body: { reason: string }
): Promise<DashboardTransitionResponse> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/reject`, {
    method: "POST",
    body: JSON.stringify(body),
    headers: ifMatch(revision),
  });
}

export async function revertDashboardToDraft(
  dashboardId: string,
  revision: number
): Promise<DashboardTransitionResponse> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/revert-to-draft`, {
    method: "POST",
    body: JSON.stringify({}),
    headers: ifMatch(revision),
  });
}

/** Unpublish through either row (the published copy or its draft). 204. */
export async function unpublishDashboard(dashboardId: string, revision: number): Promise<void> {
  await apiJson<void>(`/dashboards/${encodeURIComponent(dashboardId)}/unpublish`, {
    method: "POST",
    headers: ifMatch(revision),
  });
}

export async function listDashboardVersions(dashboardId: string): Promise<DashboardVersionDTO[]> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/versions`);
}

export async function restoreDashboardVersion(
  dashboardId: string,
  versionNumber: number,
  revision: number
): Promise<DashboardTransitionResponse> {
  return apiJson(
    `/dashboards/${encodeURIComponent(dashboardId)}/versions/${versionNumber}/restore`,
    { method: "POST", body: JSON.stringify({}), headers: ifMatch(revision) }
  );
}

export async function listDashboardReviewQueue(): Promise<{
  items: DashboardReviewQueueItemDTO[];
  total: number;
}> {
  return apiJson(`/dashboards/review-queue`);
}

export async function getDashboardReviewQueueCount(): Promise<{ count: number }> {
  return apiJson(`/dashboards/review-queue/count`);
}

export async function updateDashboard(
  dashboardId: string,
  payload: {
    name?: string;
    description?: string;
    /** A `tablerIconMap` key; "" clears it (the default icon draws). */
    icon?: string;
    is_default?: boolean;
    tags?: string[];
    hero_item_id?: string;
    /** Where a personal draft publishes (B1); null makes it personal again. */
    publish_target_scope?: PublishTargetScope | null;
    publish_target_workspace_id?: string | null;
    show_filter_bar?: boolean;
    confirm_click_filter?: boolean;
    /** One theme colour (ADR-0380): merged server-side under a row lock. */
    palette_slot?: { slot: number; hex: string };
    /** `null` = "Use the account palette". */
    theme_colors?: null;
    /** Dashboard series pins (ADR-0476): 1 to 50 per call, a ref at most
     *  once; `color: null` removes that pin. Merged server-side under a row
     *  lock, like `palette_slot`. */
    series_colors?: SeriesColorWrite[];
    /** The stored default period (ADR-0496 D3). PRESENCE decides: null removes it. */
    default_period?: ItemPeriod | null;
    /** A report's brief: the three keys replaced, null clears them. A
     *  dashboard answers 422. */
    report_brief?: ReportBriefWrite | null;
    /** A report's lens (public id); null or "" unbinds. */
    lens_id?: string | null;
    /** The dashboard's tile frame. PRESENCE decides: null follows the account. */
    tile_title_rule?: boolean | null;
    tile_corners?: TileCornersDTO | null;
    tile_outline?: boolean | null;
    tile_subtitle_width?: TileSubtitleWidthDTO | null;
  } & Partial<TextStylesWrite>
    /** The dashboard's control style (plan 2026-10-19-control-block-styling):
     *  `control_<key>`, PRESENCE decides, null clears. */
    & Partial<Record<`control_${ControlStyleKey}`, string | number | null>>
): Promise<DashboardDTO> {
  return apiJson<DashboardDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteDashboard(dashboardId: string): Promise<void> {
  await apiNoContent(`/dashboards/${encodeURIComponent(dashboardId)}`, {
    method: "DELETE",
  });
}

// Hide / unhide a Verity dashboard for the caller. Through the CLIENT
// wrapper, not the dashboard-mutation one: this changes a personal view
// preference, not the dashboard, so it must not fire the review-state
// refresh that every real mutation triggers (ADR-0228).
export async function hideDashboard(dashboardId: string): Promise<void> {
  await clientNoContent(`/dashboards/${encodeURIComponent(dashboardId)}/hidden`, {
    method: "PUT",
  });
}

export async function unhideDashboard(dashboardId: string): Promise<void> {
  await clientNoContent(`/dashboards/${encodeURIComponent(dashboardId)}/hidden`, {
    method: "DELETE",
  });
}

// ==========================================
// TOPIC API
// ==========================================

export async function listTopics(dashboardId: string): Promise<DashboardTopicDTO[]> {
  return apiJson<DashboardTopicDTO[]>(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics`
  );
}

export async function createTopic(
  dashboardId: string,
  payload: {
    name: string;
    description?: string;
    icon?: string;
    sort_order?: number;
  }
): Promise<DashboardTopicDTO> {
  return apiJson<DashboardTopicDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function updateTopic(
  dashboardId: string,
  topicId: string,
  payload: {
    name?: string;
    description?: string;
    icon?: string;
    sort_order?: number;
    /** null = follow the dashboard's choice again; omit to leave it. */
    show_filter_bar?: boolean | null;
    /** null = an endless tab again; omit to leave it. */
    page_format?: PageFormatKey | null;
  }
): Promise<DashboardTopicDTO> {
  return apiJson<DashboardTopicDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics/${encodeURIComponent(topicId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

/**
 * Persist the dashboard's whole tab order: every topic id exactly once, in the
 * order they should appear. One request rather than a PATCH per topic — those
 * are not atomic and the PATCH route writes `sort_order` raw, so a half-applied
 * sequence can tie and the bar sorts arbitrarily. Answers with the topics in
 * the new order; callers reconcile from that rather than refetching.
 */
export async function reorderTopics(
  dashboardId: string,
  topicIds: string[]
): Promise<DashboardTopicDTO[]> {
  return apiJson<DashboardTopicDTO[]>(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics/order`,
    {
      method: "PUT",
      body: JSON.stringify({ topic_ids: topicIds }),
    }
  );
}

export async function deleteTopic(
  dashboardId: string,
  topicId: string
): Promise<void> {
  await apiNoContent(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics/${encodeURIComponent(topicId)}`,
    { method: "DELETE" }
  );
}

// ==========================================
// TAB API
// ==========================================

export async function createTab(
  dashboardId: string,
  topicId: string,
  payload: {
    name: string;
    sort_order?: number;
  }
): Promise<DashboardTabDTO> {
  return apiJson<DashboardTabDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics/${encodeURIComponent(topicId)}/tabs`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function updateTab(
  dashboardId: string,
  tabId: string,
  payload: {
    name?: string;
    sort_order?: number;
    /** The package new blocks on this tab start from (sp_... public id).
     *  Explicit `null` clears it; leaving the key out keeps what is there
     *  (the backend applies it by `model_fields_set`). */
    default_package_id?: string | null;
    /** The source instance of that package (ds_... public id); same
     *  null-versus-absent rule. */
    default_data_source_id?: string | null;
  }
): Promise<DashboardTabDTO> {
  return apiJson<DashboardTabDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteTab(
  dashboardId: string,
  tabId: string
): Promise<void> {
  await apiNoContent(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}`,
    { method: "DELETE" }
  );
}

// ==========================================
// ITEM API
// ==========================================

export async function createItem(
  dashboardId: string,
  tabId: string,
  payload: {
    name?: string;
    chart_spec_json?: Record<string, unknown>;
    cached_data_json?: Record<string, unknown>;
    query_sql?: string;
    query_ir_json?: Record<string, unknown>;
    block_type?: "text" | "heading" | "kpi" | "chart" | "table"; // block <-> intent check on the backend
    data_source_id?: string;
    conversation_id?: string | null; // Link to scoped conversation (conv_...)
    layout_x?: number;
    layout_y?: number;
    layout_w?: number;
    layout_h?: number;
    /** The layout mode the page renders; required by the server for a write
     *  that places a block on a free dashboard (D8.1). */
    layout_mode?: LayoutMode;
    refresh_mode?: RefreshModeType;
  }
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/items`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export async function updateItem(
  dashboardId: string,
  tabId: string,
  itemId: string,
  payload: {
    name?: string;
    chart_spec_json?: Record<string, unknown>;
    // The tile's query (AbstractQueryIR as JSON). Accepted by the PATCH since
    // the composition builder (plan 2026-09-02, C5): a self-built block saves
    // the spec AND the query that produced it in one call, so the tile is
    // refreshable the moment it is saved.
    query_ir_json?: Record<string, unknown>;
    block_type?: "text" | "heading" | "kpi" | "chart" | "table"; // block <-> intent check on the backend
    // The block's saved-filter references (ADR-0232): the whole list when
    // present, absent = unchanged. The backend validates every id against the
    // dashboard's saved filters, clears the block's caches and bumps the
    // dashboard's filters_revision on a change.
    filter_refs?: string[];
    conversation_id?: string | null; // Link to scoped conversation (conv_...)
    /** The block's binding. `undefined` (omitted) leaves it as it is; an
     *  EXPLICIT `null` clears it, which is how a block moves from a
     *  per-source query to an account-level mart. The backend reads the
     *  field's PRESENCE (`model_fields_set`), so the two are not the same
     *  request — see api/routes/dashboard_routes.py::update_item. */
    data_source_id?: string | null;
    layout_x?: number;
    layout_y?: number;
    layout_w?: number;
    layout_h?: number;
    /** The layout mode the page renders, with any layout field (D8.1). */
    layout_mode?: LayoutMode;
    /** The line under the title. PRESENCE decides: absent leaves it alone,
     *  `""` or null clears it and its variables. */
    subtitle?: string | null;
    /** The subtitle's variables (`{language, variables}`). Read only when
     *  `subtitle` is sent: always send the two together. */
    subtitle_smart_text?: { language: string; variables: Record<string, unknown> } | null;
    /** The block's tile frame. PRESENCE decides: null follows the dashboard. */
    tile_title_rule?: boolean | null;
    tile_corners?: TileCornersDTO | null;
    tile_outline?: boolean | null;
    tile_subtitle_width?: TileSubtitleWidthDTO | null;
  } & Partial<TextStylesWrite>
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

/**
 * Add a whole row of empty blocks at the bottom of a tab.
 *
 * The shape is the server's: `template` names one of the row layouts in the
 * generated composition-layout spec and `count` how many blocks it gets (the
 * template's own default when omitted). The placeholders come back in the
 * order they were placed, left to right, so the caller can append them and
 * scroll to the first one. A count the template does not allow is a 422 whose
 * detail names the allowed ones.
 */
export async function addRow(
  dashboardId: string,
  tabId: string,
  template: string,
  count?: number
): Promise<DashboardItemDTO[]> {
  return apiJson<DashboardItemDTO[]>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/rows`,
    {
      method: "POST",
      body: JSON.stringify({ template, count }),
    }
  );
}

export async function deleteItem(
  dashboardId: string,
  tabId: string,
  itemId: string
): Promise<void> {
  await apiNoContent(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" }
  );
}

// ==========================================
// LAYOUT API
// ==========================================

/** A dashboard's layout mode (D2). */
export type LayoutMode = "grid" | "free";

export type LayoutUpdate = {
  item_id: string;
  layout_x: number;
  layout_y: number;
  layout_w: number;
  layout_h: number;
};

/** `layoutMode` is the mode the page RENDERS (D8.1): the server refuses the
 *  write with `layout_mode_changed` when the stored mode differs, so a grid
 *  page left open across a switch cannot compact a free layout away. */
export async function updateLayout(
  dashboardId: string,
  tabId: string,
  items: LayoutUpdate[],
  layoutMode: LayoutMode = "grid",
): Promise<{ success: boolean; updated_count: number }> {
  return apiJson<{ success: boolean; updated_count: number }>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/layout`,
    {
      method: "PATCH",
      body: JSON.stringify({ items, layout_mode: layoutMode }),
    }
  );
}

/** Switch a dashboard between grid and free layout (D5). The answer is the
 *  whole dashboard: to grid the server snaps and compacts every tab, so the
 *  caller replaces its state from it instead of flipping optimistically. */
export async function updateLayoutMode(
  dashboardId: string,
  layoutMode: LayoutMode,
): Promise<DashboardDTO> {
  return apiJson<DashboardDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/layout-mode`,
    {
      method: "PATCH",
      body: JSON.stringify({ layout_mode: layoutMode }),
    }
  );
}

export type StackingOp = "front" | "forward" | "backward" | "back";

export type StackingResult = {
  /** Every block of the tab, renumbered densely by the server. */
  items: { id: string; layout_z: number }[];
  revision: number;
};

/** Restack one block on a free dashboard (D4). The server renumbers the
 *  tab's `layout_z` in one transaction; the client applies the answer and
 *  does no z arithmetic of its own. `layoutMode` is the echo (D8.1). */
export async function updateItemStacking(
  dashboardId: string,
  tabId: string,
  itemId: string,
  op: StackingOp,
  layoutMode: LayoutMode,
): Promise<StackingResult> {
  return apiJson<StackingResult>(
    `/dashboards/${encodeURIComponent(dashboardId)}/tabs/${encodeURIComponent(tabId)}/items/${encodeURIComponent(itemId)}/stacking`,
    {
      method: "PATCH",
      body: JSON.stringify({ op, layout_mode: layoutMode }),
    }
  );
}

// ==========================================
// ITEM SETTINGS
// ==========================================

export async function updateItemSettings(
  itemId: string,
  payload: {
    refresh_mode: RefreshModeType;
  }
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/items/${encodeURIComponent(itemId)}/settings`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    }
  );
}

// ==========================================
// ITEM COLUMN FORMATS (plan 2026-09-03 table column format overrides)
// ==========================================

export type ColumnFormatsPayload = Record<
  string,
  { unit?: "none" | "K" | "M"; decimals?: number; negatives?: "minus" | "parentheses" }
>;

/** Replace a table tile's per-column unit/decimals. The server merges only
 *  the `column_formats` key of the spec; an empty object clears it. */
export async function updateItemColumnFormats(
  itemId: string,
  columnFormats: ColumnFormatsPayload,
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/items/${encodeURIComponent(itemId)}/column-formats`,
    {
      method: "PATCH",
      body: JSON.stringify({ column_formats: columnFormats }),
    }
  );
}

/** A pivot's period label style (plan 2026-10-06 pivot period labels):
 *  `null` goes back to the default (`short`, the key removed). The server
 *  writes only `period_label_style`; nothing is re-queried. */
export type PeriodLabelStylePayload = "short" | "compact" | "long" | "iso" | null;

export async function updateItemPeriodLabelStyle(
  itemId: string,
  style: PeriodLabelStylePayload,
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/items/${encodeURIComponent(itemId)}/period-label-style`,
    {
      method: "PATCH",
      body: JSON.stringify({ style }),
    }
  );
}

/** How a block names its date-part values (plan 2026-10-16 every time
 *  dimension carries its date parts, S1): `null` goes back to the default
 *  (`short`, the key removed). The server writes only `date_part_label`;
 *  nothing is re-queried. */
export type DatePartLabelPayload = "number" | "short" | "long" | null;

export async function updateItemDatePartLabel(
  itemId: string,
  label: DatePartLabelPayload,
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/items/${encodeURIComponent(itemId)}/date-part-label`,
    {
      method: "PATCH",
      body: JSON.stringify({ label }),
    }
  );
}

/** One dashboard pin write (ADR-0476): set `ref` to `color`, or remove it
 *  with `color: null`. A ref is `m:<metric>` or `d:<raw value>`; a role
 *  (`r:`) is chart-level only and the server refuses it here. */
export type SeriesColorWrite = { ref: string; color: string | null };

/** The most pins one dashboard write takes (`MAX_SERIES_COLOR_WRITES`). */
export const MAX_SERIES_COLOR_WRITES = 50;

/** A chart's stored colour setting (ADR-0476), in the server's canonical
 *  form: `custom` exactly when `palette` is "custom" (2 to 12 colours),
 *  `overrides` keyed by series ref (`m:`, `d:` or `r:`). */
export type ChartColorsPayload = {
  palette?: "account" | "theme" | "custom";
  custom?: string[];
  overrides?: Record<string, string>;
};

/** A chart's colours (ADR-0476): the whole setting, or `null` to clear it
 *  ("Reset colours of this chart"). The server merges only `chart_colors`
 *  into the stored spec under the item's row lock; nothing is re-queried. */
export async function updateItemChartColors(
  itemId: string,
  chartColors: ChartColorsPayload | null,
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(`/dashboards/items/${encodeURIComponent(itemId)}/chart-colors`, {
    method: "PATCH",
    body: JSON.stringify({ chart_colors: chartColors }),
  });
}

/** A data block's card background (plan 2026-10-17 the Format tab on every
 *  data block): `"none"` drops the card so a backdrop shape shows through,
 *  `null` puts it back. The server merges only `surface` into the stored
 *  spec under the item's row lock, like the column formats and the colour
 *  rules, so a Format-tab write can never put back a stale copy of a
 *  neighbour's write. Nothing is re-queried. */
export type SurfacePayload = "none" | null;

export async function updateItemSurface(itemId: string, surface: SurfacePayload): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(`/dashboards/items/${encodeURIComponent(itemId)}/surface`, {
    method: "PATCH",
    body: JSON.stringify({ surface }),
  });
}

/** A block's own period (ADR-0445): the window and comparison it always
 *  shows, or `null` to follow the dashboard again. The server validates it
 *  with `ItemPeriod`, resets the block's caches and answers the item. */
export async function updateItemPeriod(
  itemId: string,
  period: ItemPeriod | null,
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/items/${encodeURIComponent(itemId)}/period`,
    { method: "PATCH", body: JSON.stringify({ period }) },
  );
}

/** Which tiles a click on this one filters (plan 2026-10-05 dashboard
 *  interactivity, B5): "auto" = every tile that can take the filter; a list
 *  of block keys = only those; [] = none. */
export type InteractionTargets = "auto" | string[];

export async function updateItemInteractions(
  itemId: string,
  targets: InteractionTargets,
): Promise<DashboardItemDTO> {
  return apiJson<DashboardItemDTO>(
    `/dashboards/items/${encodeURIComponent(itemId)}/interactions`,
    { method: "PUT", body: JSON.stringify({ targets }) },
  );
}

// ==========================================
// REFRESH ITEM
// ==========================================

export type ComparisonData = {
  rows: Record<string, unknown>[];
  columns: string[];
  row_count: number;
  /** The REQUESTED window: the comparison cache identity on both sides.
   *  Never shortened — `covered_range` says which days a total summed. */
  date_range: {
    start_date: string;
    end_date: string;
    timezone: string;
  };
  /**
   * What a comparison TOTAL covers (ADR-0310), on single-value, categorical
   * and breakdown legs: `clipped` to the days the main total has data for,
   * `full`, `empty` (the main window has no data yet: `rows: []`, and the
   * card draws a reason, not -100%), or `unknown` (not clipped). Absent from
   * a backend that predates it and from time-series legs — read as `unknown`.
   */
  coverage?: Coverage;
  /** The days the total summed; null for `empty` and `unknown`. */
  covered_range?: { start_date: string; end_date: string } | null;
  /**
   * Present only on the target leg (`target_data` / `target_cached_data_json`).
   * Same envelope as a period comparison plus what only a plan has: the kind
   * (target / budget / guardrail), its comparator, the allocation method, and
   * which version the number came from (services/targets/dashboard_leg.py).
   *
   * `rows: []` with a `target.reason` is the empty sentinel: the tile is not
   * covered by the plan, and the renderer draws nothing rather than a zero.
   */
  target?: TargetComparisonBlock | null;
};

export type RefreshItemResponse = {
  chart_spec_json: Record<string, unknown>;
  chart_config_json?: Record<string, unknown> | null;
  data_json?: Record<string, unknown> | null;
  cached_data_json: Record<string, unknown>;
  row_count: number;
  row_count_truncated: boolean;
  last_refreshed_at: string;
  cache_hit?: boolean;
  executed_sql?: string; // Only in dev/debug
  comparison_data?: ComparisonData | null;
  comparison_error?: string | null;
  /** The target leg when asked for beside the comparison dates (W1.8): the
   *  window stays in `comparison_data`, the plan lands here. */
  target_data?: ComparisonData | null;
  target_error?: string | null;
  /** The OTHER shifted period on a metric card, when the request named
   *  `other_comparison_*` (plan 2026-09-14-the-plan-leg-on-every-surface,
   *  S4). ABSENT (not null) from a backend that predates the field — the
   *  merge reads that as "unavailable", never as an empty period. */
  other_comparison_data?: ComparisonData | null;
  other_comparison_error?: string | null;
  available_time_grains?: string[];
  data_freshness?: "managed" | "ttl" | null;
  cache_valid_until?: string | null;
  time_dimension_alias?: string | null;
  column_agg_hints?: Record<string, "sum" | { type: "ratio"; numerator: string; denominator: string }> | null;
  /**
   * This tile has no date dimension, so the requested range was NOT applied —
   * every requested range is a cache hit for such a dataset. Anything that
   * renders a period label must suppress it.
   */
  timeless?: boolean;
  /** A smart text tile only (plan 2026-10-16-a-smart-text-block, S4): the
   *  server's text per variable and the period it named, or
   *  `{variables: {}, period: null, error: "unresolved"}`. Read through
   *  `smartTextModel.parseSmartTextAnswer`; absent from an older backend. */
  smart_text?: Record<string, unknown> | null;
  /** A chart or table tile whose subtitle has variables (plan 2026-10-16-a-
   *  block-subtitle): the same shape as `smart_text`. Never persisted on the
   *  item and never cached in the browser (the period it names belongs to
   *  the preset); absent when the subtitle has no variables. */
  subtitle?: Record<string, unknown> | null;
};

export async function refreshItem(
  itemId: string,
  payload: {
    force?: boolean; // false = data-only, true = reformat
    skip_cache?: boolean; // true = bypass server-side cache
    start_date?: string; // ISO date (must be paired with end_date)
    end_date?: string;
    timezone?: string;
    comparison_start_date?: string;
    comparison_end_date?: string;
    /**
     * The account's active target plan, resolved for the range being
     * refreshed. Sent only NEXT TO the comparison dates (W1.8): the window
     * keeps `comparison_data` and the plan lands in `target_data`. On a
     * dashboard the plan is never the comparison alone (ADR-0269).
     */
    comparison_mode?: "target";
    /** The other shifted period beside the comparison dates — resolved for
     *  metric cards only, answered in `other_comparison_data` (S4). */
    other_comparison_start_date?: string;
    other_comparison_end_date?: string;
    time_grain?: string;
    /** A pivot viewer's own period grain (plan 2026-10-06 pivot period
     *  grain, D3). Honoured only on a tile with `viewer_choice`; a grain that
     *  differs from the tile's default is a transient view. Single-item
     *  refresh only: bulk refresh does not take it. */
    viewer_time_grain?: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
    dimension_filters?: DimensionFilters; // aggregate-dashboard filter (W2.5)
    /**
     * A LOOK, not a write. The tile is queried over the requested period and
     * the result is returned, but nothing is persisted — no cached data, no
     * chart spec, no timestamps. Use this whenever you render a tile over a
     * period the user did not choose for the dashboard itself: without it,
     * "show me this chart over July" rewrites the tile's saved cache for
     * everyone who opens that dashboard.
     */
    transient?: boolean;
    /** A smart text tile only (plan 2026-10-16-a-smart-text-block, S4): the
     *  browser's `TimeRange.preset`, so the server can name the period. Not
     *  trusted: the server keeps it only when it describes the dates. */
    period_preset?: string;
  }
): Promise<RefreshItemResponse> {
  return apiPostRead<RefreshItemResponse>(
    `/dashboards/items/${encodeURIComponent(itemId)}/refresh`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

// ==========================================
// DRILL DOWN ITEM
// ==========================================

/** One level of a recursive drill: the tile's row first, then each level's
 *  breakdown key and the clicked value. */
export type DrillPathEntry = { key: string; value: string };

export type DrilldownRequest = (
  | {
      /** The parent row's index value, e.g. the channel that was clicked. */
      parent_value: string;
      path?: never;
    }
  | {
      /** 1 to 3 levels; required below the tile's own row. */
      path: DrillPathEntry[];
      parent_value?: never;
    }
) & {
  /** Group the children by this instead of the tile's default; required
   *  below the first level. */
  breakdown_key?: string;
  start_date: string; // ISO date (YYYY-MM-DD)
  end_date: string;
  timezone: string;
  comparison_start_date?: string;
  comparison_end_date?: string;
  dimension_filters?: DimensionFilters;
  limit?: number;
};

export type DrilldownRemainder = {
  /** Same shape as a child row; folds every child beyond `limit`. */
  row: Record<string, unknown>;
  hidden_count: number;
};

export type DrilldownResponse = {
  breakdown_key: string;
  breakdown_label: string;
  columns: string[];
  /**
   * Child rows, RAW numeric, in server order. The child label rides under the
   * PARENT tile's `chart_index_by` key (e.g. `channel`), not under
   * `breakdown_key` — that is what lets the table format a child cell with the
   * exact same column rules as a parent cell.
   */
  rows: Record<string, unknown>[];
  comparison_rows: Record<string, unknown>[] | null;
  remainder: DrilldownRemainder | null;
  /**
   * Live sum over ALL children (hidden ones included), restricted to summable
   * metrics — ratios are absent by construction, because summing them is
   * meaningless. The renderer compares this with the cached parent row to spot
   * a cache that has since been overtaken.
   */
  children_total: Record<string, number | null>;
  children_comparison_total: Record<string, number | null> | null;
  truncated: boolean;
  hidden_count: number;
};

/**
 * Children of one parent row, queried live. A LOOK, not a write: nothing is
 * persisted, no cache entry is rewritten, exactly like a transient refresh.
 */
export async function drilldownItem(
  itemId: string,
  payload: DrilldownRequest
): Promise<DrilldownResponse> {
  return apiPostRead<DrilldownResponse>(
    `/dashboards/items/${encodeURIComponent(itemId)}/drilldown`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

/** Body of `POST /dashboards/:id/dimension-values` (plan 2026-10-05
 *  dashboard interactivity, A3). `filters` are the OTHER active filters, so
 *  the list never offers a value that would empty the page. */
export type DimensionValuesRequest = {
  key: string;
  q?: string;
  topic_id?: string;
  filters?: DimensionFilters;
  start_date: string; // ISO date (YYYY-MM-DD)
  end_date: string;
  timezone: string;
  limit?: number;
};

export type DimensionValuesResponse = components["schemas"]["DimensionValuesResponse"];

/**
 * The values a viewer filter on `key` can take. A LOOK, not a write: the only
 * thing it fills is the backend's shared query cache. 422 when the key is not
 * filterable on this dashboard, 429 when rate limited.
 */
export async function fetchDimensionValues(
  dashboardId: string,
  payload: DimensionValuesRequest,
  signal?: AbortSignal,
): Promise<DimensionValuesResponse> {
  return apiPostRead<DimensionValuesResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/dimension-values`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    }
  );
}

/** One open control's options in control mode (ADR-0350, plan 2026-09-29
 *  controls A2). `unavailable` = the control has no field, or no tile it
 *  steers that this viewer may query carries the field. */
export type ControlValuesDTO = components["schemas"]["ControlValuesDTO"];

/** The control-mode body of `POST /dashboards/:id/dimension-values`: each
 *  control's field, page and targets are read server-side from its STORED
 *  spec. At most 20 distinct ids; `q` only with exactly one id. */
export type ControlValuesRequest = Pick<
  components["schemas"]["DimensionValuesRequest"],
  "start_date" | "end_date" | "timezone"
> & {
  control_item_ids: string[];
  q?: string;
  filters?: DimensionFilters;
  /** Control id -> that control's own filter set, used instead of `filters`
   *  for it (plan 2026-10-09 button slicer B2: Only relevant values). */
  control_filters?: Record<string, DimensionFilters>;
  limit?: number;
};

/** The most control ids one request may name (backend `max_length`). */
export const MAX_CONTROL_IDS_PER_REQUEST = 20;

/**
 * The options of one or more open controls, in one request. The answer's
 * `controls` holds one entry per requested id, in request order. 404 when an
 * id is not on this dashboard, 429 when rate limited.
 */
export async function fetchControlValues(
  dashboardId: string,
  payload: ControlValuesRequest,
  signal?: AbortSignal,
): Promise<DimensionValuesResponse> {
  return apiPostRead<DimensionValuesResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/dimension-values`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    }
  );
}

// ==========================================
// TILE EXPORT — XLSX
// ==========================================

/**
 * Byte ceiling the frontend enforces BEFORE posting an export.
 *
 * The hard limit is the BFF's global 1 MB JSON parser (`apps/bff/src/index.ts`
 * — the global parser on `/api/*`). A route-level parser cannot raise it: by
 * the time a dashboards route runs, the global one has already consumed the
 * stream, and that file documents the trap itself. So the ceiling is a fact of
 * the platform, not a knob, and the honest place to meet it is here — with a
 * message that offers the way out — rather than as a 413 the user has to
 * interpret.
 *
 * The margin below 1 MB covers what the JSON envelope adds on top of the rows.
 */
export const XLSX_EXPORT_MAX_BYTES = 900_000;

export type TileExportPayload = {
  headers: string[];
  columns: string[];
  rows: Record<string, unknown>[];
  title?: string;
  // A pivot's presentation (plan 2026-10-05 finance pivot, D6), spread from
  // `TileTable.pivot`; closed and length-checked by `TileExportXlsxRequest`.
} & Partial<TileTablePivot>;

/** Serialized size in BYTES — never `string.length`.
 *
 * `string.length` counts UTF-16 code units, so it under-counts exactly the
 * payloads most likely to sit near the ceiling: a `€` is 3 bytes and 1 unit,
 * an emoji 4 bytes and 2. Under-counting here would send a request the BFF
 * then rejects with a 413, which is the outcome the guard exists to prevent.
 */
export function measureExportBytes(payload: TileExportPayload): number {
  return new TextEncoder().encode(JSON.stringify(payload)).length;
}

/**
 * Render one tile's table as an .xlsx workbook and return the bytes.
 *
 * Server-side on purpose (ADR to follow with the plan): the frontend's
 * spreadsheet library was removed for a security advisory and is not coming
 * back into the bundle, and the backend already pins every text cell's type so
 * a value that looks like a formula or an Excel error code cannot be
 * reinterpreted on open.
 *
 * Throws `ApiError` with `.status` on failure — callers branch on 413.
 */
export async function exportItemXlsx(
  itemId: string,
  payload: TileExportPayload
): Promise<Blob> {
  return apiBlob(`/dashboards/items/${encodeURIComponent(itemId)}/export/xlsx`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Export a whole dashboard as code (structure and queries, never data rows).
 *
 * The BFF exposes no `Content-Disposition` to JS, so the caller names the
 * file from the dashboard name plus the format's `file_extension`.
 * Throws `ApiError` with `.status`: 403 (the format needs SQL and the caller
 * is not an owner or admin), 429 (too many exports).
 */
export async function exportDashboardCode(dashboardId: string, format: string): Promise<Blob> {
  return apiBlob(
    `/dashboards/${encodeURIComponent(dashboardId)}/export/code?format=${encodeURIComponent(format)}`
  );
}

/**
 * What the tile export menu may offer on THIS environment.
 *
 * `sheets_enabled` is honest feature detection: no export OAuth client id
 * configured means the Google Sheets entry is not rendered, rather than
 * rendered into a popup that ends on a Google error page. The endpoint is
 * therefore its own deploy preflight.
 *
 * `xlsx_enabled` reports that the backend build carries the workbook renderer.
 * It is deliberately NOT used to hide the Excel entry when this call fails —
 * a config fetch that did not answer must not take a working feature away.
 *
 * No secret travels here. An OAuth client id identifies the app to Google and
 * authorises nothing; the client secret has no part in the Sheets flow, since
 * the browser mints its own short-lived `drive.file` token.
 */
export type TileExportConfig = {
  sheets_enabled: boolean;
  xlsx_enabled: boolean;
  google_client_id?: string | null;
  /** Dashboard-as-code formats (plan 2026-10-17-dashboard-export-as-code),
   *  each with whether THIS caller may download it; the role decision is the
   *  server's, so the UI holds no role logic. */
  code_formats?: CodeExportFormat[];
  code_export_physical_allowed?: boolean;
};

/** One dashboard-as-code format (`CodeExportFormatDTO`). */
export type CodeExportFormat = {
  key: string;
  label: string;
  description: string;
  /** Includes the leading dot, e.g. ".md" or ".zip". */
  file_extension: string;
  /** Needs the SQL and the physical table names: owners and admins only. */
  requires_physical: boolean;
  available: boolean;
};

export async function fetchTileExportConfig(): Promise<TileExportConfig> {
  return apiJson<TileExportConfig>("/dashboards/export/config");
}

// ==========================================
// BULK REFRESH
// ==========================================

export type BulkRefreshResponse = {
  items: Record<string, RefreshItemResponse>;
  errors: Record<string, string>;
};

export async function bulkRefresh(
  dashboardId: string,
  payload: {
    item_ids: string[];
    start_date?: string;
    end_date?: string;
    timezone?: string;
    time_grain?: string;
    skip_db_persist?: boolean;
    comparison_start_date?: string;
    comparison_end_date?: string;
    /** See refreshItem: the target leg, without dates. */
    comparison_mode?: "target";
    /** See refreshItem: the other shifted period, metric cards only. */
    other_comparison_start_date?: string;
    other_comparison_end_date?: string;
    dimension_filters?: DimensionFilters; // aggregate-dashboard filter (W2.5)
  },
  /** Aborts the request when a newer refresh of the same view replaces it
   *  (plan 2026-10-05 dashboard interactivity, B3a). */
  signal?: AbortSignal,
): Promise<BulkRefreshResponse> {
  return apiPostRead<BulkRefreshResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/bulk-refresh`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      signal,
    }
  );
}

/** What one "Refresh data" press marked (plan 2026-10-21 refresh interval,
 *  D6). `reason` says why nothing was: no tile in scope reads a customer
 *  source, or the caller may refill none of them. */
export type RefreshDashboardDataResponse = {
  sources: number;
  skipped_tiles: number;
  reason: "no_customer_source" | "no_grant" | null;
};

/**
 * Ask for new data from the customer sources of a dashboard's tiles, or of
 * one tile (`itemId`). The server marks those sources stale; it deletes
 * nothing, so the next refresh of any period reads the warehouse. Rate
 * limited per user (429). A read in the mutation sense: it changes no
 * dashboard row, so no review-state reload follows.
 */
export async function refreshDashboardData(
  dashboardId: string,
  itemId?: string,
): Promise<RefreshDashboardDataResponse> {
  return apiPostRead<RefreshDashboardDataResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/refresh-data`,
    {
      method: "POST",
      body: JSON.stringify(itemId ? { item_id: itemId } : {}),
    },
  );
}

// ==========================================
// AGGREGATE DEFAULT VIEW (W2.7)
// ==========================================

// Set (or clear) an aggregate dashboard's account-wide default view. Pass
// `null` to clear (all values). Admin-only + account-scoped on the backend.
export async function setDefaultFilters(
  dashboardId: string,
  dimension_filters: DimensionFilters | null
): Promise<DashboardDTO> {
  return apiJson<DashboardDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/default-filters`,
    {
      method: "PUT",
      body: JSON.stringify({ dimension_filters }),
    }
  );
}

// ==========================================
// SCOPE FILTERS (ADR-0230)
// ==========================================

export type ScopeFiltersResult = {
  filters: ScopeFilterDTO[];
  filter_refs: string[];
  filters_revision: number;
};

/** Replace the dashboard's inline rows and/or saved-filter references (a
 *  list absent from `body` is unchanged). The backend clears every block's
 *  caches on the dashboard; the caller refreshes the tiles. */
export async function putDashboardScopeFilters(
  dashboardId: string,
  body: ScopeFiltersBody,
): Promise<ScopeFiltersResult> {
  return apiJson<ScopeFiltersResult>(`/dashboards/${encodeURIComponent(dashboardId)}/filters`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Replace a tab's (topic's) inline rows and/or saved-filter references. */
export async function putTopicScopeFilters(
  dashboardId: string,
  topicId: string,
  body: ScopeFiltersBody,
): Promise<ScopeFiltersResult> {
  return apiJson<ScopeFiltersResult>(
    `/dashboards/${encodeURIComponent(dashboardId)}/topics/${encodeURIComponent(topicId)}/filters`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

// --- the saved-filter catalog (ADR-0232) ---

/** Add a saved filter; the id is minted by the server. Nothing references a
 *  new entry, so nothing reloads. */
export async function createCatalogFilter(
  dashboardId: string,
  entry: { name: string; filters: ScopeFilterDTO[] },
): Promise<FilterCatalogEntryDTO> {
  return apiJson<FilterCatalogEntryDTO>(`/dashboards/${encodeURIComponent(dashboardId)}/filter-catalog`, {
    method: "POST",
    body: JSON.stringify(entry),
  });
}

/** Rename a saved filter and/or change its clauses (a field absent is
 *  unchanged). A clause change clears every referencing block's caches on
 *  the server; the caller refreshes the tiles. */
export async function updateCatalogFilter(
  dashboardId: string,
  filterId: string,
  patch: { name?: string; filters?: ScopeFilterDTO[] },
): Promise<FilterCatalogEntryDTO> {
  return apiJson<FilterCatalogEntryDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/filter-catalog/${encodeURIComponent(filterId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

/** Delete a saved filter: the server detaches it from the dashboard, every
 *  tab and every block first. */
export async function deleteCatalogFilter(dashboardId: string, filterId: string): Promise<void> {
  await apiNoContent(
    `/dashboards/${encodeURIComponent(dashboardId)}/filter-catalog/${encodeURIComponent(filterId)}`,
    { method: "DELETE" },
  );
}

/** The dimensions a scope filter may name, with coverage over the blocks in
 *  scope: the tab's with `topicId`, else the whole dashboard's. */
export async function getScopeFilterFields(
  dashboardId: string,
  topicId?: string | null,
): Promise<ScopeFilterFieldDTO[]> {
  const query = topicId ? `?topic_id=${encodeURIComponent(topicId)}` : "";
  return apiJson<ScopeFilterFieldDTO[]>(
    `/dashboards/${encodeURIComponent(dashboardId)}/filter-fields${query}`,
  );
}

export type DashboardDefinitionDTO = components["schemas"]["DashboardDefinitionDTO"];
export type DashboardDefinitionsResponse = components["schemas"]["DashboardDefinitionsResponse"];

/** What the dashboard's metrics and dimensions mean: label, description,
 *  formula and sources, for the view-mode "About this data" drawer. A read;
 *  no warehouse query. */
export async function getDashboardDefinitions(
  dashboardId: string,
): Promise<DashboardDefinitionsResponse> {
  return apiJson<DashboardDefinitionsResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/definitions`,
  );
}

// ==========================================
// LINK DATA SOURCE
// ==========================================

export async function linkDashboardDataSource(
  dashboardId: string,
  dataSourceId: string
): Promise<DashboardDTO> {
  return apiJson<DashboardDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/link-data-source`,
    {
      method: "PATCH",
      body: JSON.stringify({ data_source_id: dataSourceId }),
    }
  );
}

// ==========================================
// CONVERSATION MESSAGES (for insight chats)
// ==========================================

export type ConversationMessageDTO = {
  id: string;
  sender: "user" | "ai";
  content: string;
  timestamp: string;
  chart_data?: Record<string, unknown>;
  table_data?: Record<string, unknown>;
  query_sql?: string | null;
  /** Structured answer sections (VER-256) — the backend already returns these. */
  sections?: MessageSection[];
  /** Genuine model prose after the sections (assembler-verified; never synthetic). */
  closing_text?: string | null;
  /**
   * Periodic analyst: deterministic sentence naming the sources that were NOT
   * analysed. Absent on every other message; rendered above the sections.
   */
  coverage_notice?: string | null;
  /**
   * Chart analyses only: the dashboard period this question was asked in.
   * Absent on every other message, and on analysis messages written before
   * the period snapshot shipped — treat undefined as "unknown period", never
   * as "today". The backend validates the shape before serving it.
   */
  dashboard_filters?: {
    start_date: string;
    end_date: string;
    time_grain: string | null;
    /** The tile's dimension filters when the question was asked (a control
     *  block's model, a tab filter). Absent when there were none. */
    dimension_filters?: DimensionFilters;
  } | null;
  /**
   * USER messages asked in the MAIN chat on a chart conversation: the chart
   * context that turn ran under. Absent on drawer turns (those carry
   * `dashboard_filters`) and on every other message.
   */
  tile_context?: { mode: string; fallback?: string } | null;
  /** Confirm cards the answer carried (e.g. the drawer's continue-in-chat). */
  action_proposals?: ActionProposal[] | null;
};

/**
 * Fetch messages for a conversation.
 * Used to load chat history when editing a dashboard item.
 * @param conversationId The public_id of the conversation (conv_...)
 */
export async function getConversationMessages(
  conversationId: string
): Promise<ConversationMessageDTO[]> {
  return apiJson<ConversationMessageDTO[]>(
    `/chats/conversations/${encodeURIComponent(conversationId)}/messages`
  );
}

// ==========================================
// PRODUCT HOVER IMAGES (Shopify / WooCommerce)
// ==========================================

/** Whose products mart answers an image lookup. */
export type ProductImageConnector = "SHOPIFY" | "WOOCOMMERCE";

/**
 * Fetch product hover-images for a bounded set of product IDs from the
 * account's Shopify or WooCommerce products mart.
 *
 * Progressive enhancement: the dashboard renders fully without images, so this
 * never throws. ANY failure (network, 4xx/5xx, unprovisioned account) resolves
 * to an empty map and the UI simply shows no hover affordance — no error state,
 * no retry storm. The backend returns `{ images: { "<product_id>": "<https url>" } }`
 * and returns an empty map on any degraded state. The path keeps its Shopify
 * name: the backend reads `connector` from the body and defaults to SHOPIFY.
 */
export async function fetchProductImages(
  connector: ProductImageConnector,
  productIds: number[]
): Promise<Record<string, string>> {
  try {
    const data = await apiPostRead<{ images?: Record<string, string> }>(
      `/dashboards/shopify/product-images`,
      {
        method: "POST",
        body: JSON.stringify({ connector, product_ids: productIds }),
      }
    );
    return data?.images ?? {};
  } catch {
    return {};
  }
}

// ==========================================
// AD CREATIVE HOVER PREVIEWS (Meta / Google Ads)
// ==========================================

/** Connectors whose dashboards carry ad-level rows the endpoint can preview. */
export type AdCreativeConnector = "GOOGLE_ADS" | "FACEBOOK";

type AdCreativePreviewPayload = {
  ad_type?: string | null;
  ad_name?: string | null;
  headlines?: string[] | null;
  descriptions?: string[] | null;
  display_url?: string | null;
  image_url?: string | null;
  youtube_video_id?: string | null;
};

/**
 * Fetch reconstructed ad creatives for a bounded set of ad IDs.
 *
 * Same progressive-enhancement contract as the product images above: never
 * throws, any failure resolves to an empty map, and the dashboard renders
 * exactly as it does today. Callers MUST treat an empty map as "unknown", not
 * as "these ads have no creative" — the backend fails soft with 200 + `{}`.
 */
export async function fetchAdCreatives(
  connector: AdCreativeConnector,
  adIds: string[]
): Promise<Record<string, RowPreview>> {
  try {
    const data = await apiPostRead<{ previews?: Record<string, AdCreativePreviewPayload> }>(
      `/dashboards/ad-creatives`,
      {
        method: "POST",
        body: JSON.stringify({ connector, ad_ids: adIds }),
      }
    );
    const out: Record<string, RowPreview> = {};
    for (const [adId, p] of Object.entries(data?.previews ?? {})) {
      out[adId] = {
        kind: "ad",
        adType: p?.ad_type ?? null,
        adName: p?.ad_name ?? null,
        headlines: Array.isArray(p?.headlines) ? p.headlines : [],
        descriptions: Array.isArray(p?.descriptions) ? p.descriptions : [],
        displayUrl: p?.display_url ?? null,
        imageUrl: p?.image_url ?? null,
        youtubeVideoId: p?.youtube_video_id ?? null,
      };
    }
    return out;
  } catch {
    return {};
  }
}


/** Where each {metric_key, dataset_key} pair is already shown on a dashboard
 * the caller may open. Batched on purpose: one rail is one call — the backend
 * rate-limiter buckets per (IP, path), so one request per finding would spend
 * N tokens per page view from a shared office IP.
 *
 * The response is aligned on request index, `null` where nothing resolved.
 * Do NOT filter the nulls out client-side before zipping — the alignment IS
 * the contract. */
export type MetricDeeplink = { dashboard_id: string; tab_id: string };

export async function resolveMetricDeeplinks(
  pairs: Array<{ metric_key: string; dataset_key: string }>,
): Promise<Array<MetricDeeplink | null>> {
  if (pairs.length === 0) return [];
  const qs = new URLSearchParams();
  for (const p of pairs) qs.append("metric_keys", p.metric_key);
  for (const p of pairs) qs.append("dataset_keys", p.dataset_key);
  return apiJson(`/dashboards/resolve-metric?${qs}`);
}

// ---------------------------------------------------------------------------
// Edit history: Undo / Redo (plan 2026-09-03 the-block-editor-saves-as-you-go,
// ADR-0220). The stack lives on the server, per (dashboard, user); the client
// only asks what the two buttons would do, and says "undo" or "redo".
// ---------------------------------------------------------------------------

/** What the Undo and Redo buttons show: the label of the step each would
 *  apply, or null for a button with nothing to do. */
export type EditStepsDTO = {
  undo: string | null;
  redo: string | null;
  undo_depth: number;
  redo_depth: number;
  // The review state after a step (ADR-0228): what the page carries onto the
  // dashboard after every mutation. Optional on the type because an older
  // backend answers without them; the page merges only what is present.
  revision?: number;
  review_status?: DashboardReviewStatus;
  permissions?: DashboardPermissions;
};

export type EditStepResultDTO = {
  label: string;
  restored: number;
  removed: number;
  revision: number;
  /** Block keys the step touched. The item DTO carries no block key, so the
   *  page closes any open editor after a step rather than matching on these. */
  touched_keys: string[];
};

export async function getEditSteps(dashboardId: string): Promise<EditStepsDTO> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/edit-steps`);
}

export async function undoEditStep(dashboardId: string): Promise<EditStepResultDTO> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/undo`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function redoEditStep(dashboardId: string): Promise<EditStepResultDTO> {
  return apiJson(`/dashboards/${encodeURIComponent(dashboardId)}/redo`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// Data sources shared through the dashboard (plan 2026-10-05 dashboard data
// sources, ADR-0346). An attachment shares the ADDER's current grant with the
// dashboard's viewers; it is re-checked on every use, so `status` says whether
// it still works and, when not, why. Editors and reviewers only: a plain
// viewer gets a 403.
// ---------------------------------------------------------------------------

export type DashboardDataSourceStatus =
  | "active"
  | "lapsed_adder_lost_access"
  | "lapsed_adder_removed"
  | "source_inactive"
  | "product_not_entitled"
  | "suspended_by_account_setting";

/** The generated DTO, with `status` narrowed to the values the backend
 *  documents and `kind` to its two (the schema types both as a plain string).
 *  An unknown status is still possible over the wire, so readers keep a
 *  fallback. A `product` row (plan phase 2: an account-level product) has
 *  `aggregate_connector_type` and a null `data_source_id`; a `source` row the
 *  other way round. */
export type DashboardDataSourceDTO = Omit<
  components["schemas"]["DashboardDataSourceDTO"],
  "status" | "kind"
> & {
  kind: "source" | "product";
  status: DashboardDataSourceStatus | (string & {});
  /** How many tiles on this dashboard read the source (absent from an
   *  older backend). */
  tile_count?: number;
  /** Which tables (logical datasets) of a source are shared (plan phase 3):
   *  null = every table, including tables added later; a list = exactly
   *  those. Null for a product. Optional until the types are regenerated. */
  dataset_keys?: string[] | null;
  /** How many tables the source has in all, for "N of M tables". Null for a
   *  product. */
  dataset_total?: number | null;
};

/** A pending request to share data sources on this dashboard (plan
 *  2026-10-19 share-sources request, D7). Nothing is decided from the
 *  dashboard: the person reviews it on `/requests/{request_id}`. */
export type PendingShareRequest = {
  /** `areq_...` */
  request_id: string;
  /** Null when the requester is no longer a member. */
  requested_by_name: string | null;
  requested_via: "mcp" | "verity";
  item_count: number;
  expires_at: string;
};

export type DashboardDataSourcesResponse = {
  data_sources: DashboardDataSourceDTO[];
  /** The account allows sharing data sources through dashboards. */
  sharing_enabled: boolean;
  /** Pending share requests (empty when none). Optional so a list cached or
   *  mocked without it keeps working. */
  pending_requests?: PendingShareRequest[];
};

export async function listDashboardDataSources(
  dashboardId: string,
): Promise<DashboardDataSourcesResponse> {
  return apiJson<DashboardDataSourcesResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/data-sources`,
  );
}

/** What an attach or detach names: one of the caller's own sources, or one
 *  of the account-level products the caller is granted. */
export type DashboardDataSourceTarget =
  | {
      kind: "source";
      dataSourceId: string;
      /** Attach only: which tables to share (plan phase 3). Absent or null =
       *  every table, including later ones; a non-empty list = exactly those. */
      datasetKeys?: string[] | null;
    }
  | { kind: "product"; connector: string };

/** Attach one of the caller's own sources or products. The body carries
 *  exactly one of `data_source_id` / `aggregate_connector_type`. 404: the
 *  caller cannot query it; 409: sharing is turned off for the account (or the
 *  product is not built yet); 422: a managed dashboard. */
export async function addDashboardDataSource(
  dashboardId: string,
  target: DashboardDataSourceTarget,
): Promise<DashboardDataSourcesResponse> {
  const body =
    target.kind === "product"
      ? { aggregate_connector_type: target.connector }
      : target.datasetKeys
        ? { data_source_id: target.dataSourceId, dataset_keys: target.datasetKeys }
        : { data_source_id: target.dataSourceId };
  return apiJson<DashboardDataSourcesResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/data-sources`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Change which tables of a shared source the dashboard shares (plan phase
 *  3): a list, or null for every table including later ones. Any editor may
 *  narrow; widening needs the caller's own access to the source (403). */
export async function updateDashboardDataSourceTables(
  dashboardId: string,
  dataSourceId: string,
  datasetKeys: string[] | null,
): Promise<DashboardDataSourcesResponse> {
  return apiJson<DashboardDataSourcesResponse>(
    `/dashboards/${encodeURIComponent(dashboardId)}/data-sources/${encodeURIComponent(dataSourceId)}`,
    { method: "PATCH", body: JSON.stringify({ dataset_keys: datasetKeys }) },
  );
}

export async function removeDashboardDataSource(
  dashboardId: string,
  target: DashboardDataSourceTarget,
): Promise<DashboardDataSourcesResponse> {
  const base = `/dashboards/${encodeURIComponent(dashboardId)}/data-sources`;
  const path =
    target.kind === "product"
      ? `${base}/products/${encodeURIComponent(target.connector)}`
      : `${base}/${encodeURIComponent(target.dataSourceId)}`;
  return apiJson<DashboardDataSourcesResponse>(path, { method: "DELETE" });
}

/** The account's "Share data sources through dashboards" setting, as any
 *  member reads it. `enabled` is the effective answer: `available` (the
 *  platform offers it to this account) AND `account_setting` (the admin's
 *  choice). */
export type DashboardSourceSharingDTO = {
  enabled: boolean;
  available: boolean;
  account_setting: boolean;
};

export async function getDashboardSourceSharing(): Promise<DashboardSourceSharingDTO> {
  return apiJson<DashboardSourceSharingDTO>(`/accounts/me/dashboard-source-sharing`);
}

/** Account OWNER/ADMIN only; the backend answers 403 otherwise. */
export async function setDashboardSourceSharing(
  enabled: boolean,
): Promise<DashboardSourceSharingDTO> {
  return apiJson<DashboardSourceSharingDTO>(`/accounts/me/dashboard-source-sharing`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

// ---------------------------------------------------------------------------
// Custom metrics made on a dashboard (plan 2026-10-13 dashboard calculated
// metrics, D3). A builder makes a metric on a table the dashboard shares; it
// is dashboard-level (this dashboard's draft and published copy have it at
// once) until an account admin makes it available everywhere (promote). Every
// gate is the backend's: edit access, the build capability, an unmanaged
// dashboard, sharing on, and the table shared right now.
// ---------------------------------------------------------------------------

export type CreateDashboardMetricRequest = components["schemas"]["CreateDashboardMetricRequest"];
export type UpdateDashboardMetricRequest = components["schemas"]["UpdateDashboardMetricRequest"];
export type DashboardMetricDTO = components["schemas"]["DashboardMetricDTO"];
export type MetricFormDTO = components["schemas"]["MetricFormDTO"];

/** Which table a metric belongs to: a table of a shared source, or of a
 *  shared account-level product. */
export type DashboardMetricTable =
  | { kind: "source"; dataSourceId: string; datasetKey: string }
  | { kind: "product"; connector: string; datasetKey: string };

/** The table as the routes take it: exactly one of `data_source_id` /
 *  `aggregate_connector_type`, plus `dataset_key`. */
export function dashboardMetricTableFields(table: DashboardMetricTable): {
  data_source_id?: string;
  aggregate_connector_type?: string;
  dataset_key: string;
} {
  return table.kind === "product"
    ? { aggregate_connector_type: table.connector, dataset_key: table.datasetKey }
    : { data_source_id: table.dataSourceId, dataset_key: table.datasetKey };
}

/** What the metric form offers for one table this dashboard shares: measure
 *  fields, dimensions and the metrics visible here. Never physical columns. */
export async function getDashboardMetricForm(
  dashboardId: string,
  table: DashboardMetricTable,
): Promise<MetricFormDTO> {
  const params = new URLSearchParams(
    Object.entries(dashboardMetricTableFields(table)).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
  return apiJson<MetricFormDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/metric-form?${params.toString()}`,
  );
}

/** Make a dashboard-level metric. The backend generates the key from the
 *  label. 403: a gate refused (the reason is named); 422: the definition
 *  cannot be saved. */
export async function createDashboardMetric(
  dashboardId: string,
  body: CreateDashboardMetricRequest,
): Promise<DashboardMetricDTO> {
  return apiJson<DashboardMetricDTO>(`/dashboards/${encodeURIComponent(dashboardId)}/metrics`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Change a dashboard-level metric of this dashboard. A key left out keeps
 *  its value; an explicit null removes that part. */
export async function updateDashboardMetric(
  dashboardId: string,
  metricId: string,
  body: UpdateDashboardMetricRequest,
): Promise<DashboardMetricDTO> {
  return apiJson<DashboardMetricDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/metrics/${encodeURIComponent(metricId)}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

/** What a refused delete names (409 `detail`): the dashboards and reports
 *  the caller may open, and only a count for the rest. */
export type MetricInUseDetail = {
  code?: string | null;
  message?: string | null;
  references: Array<{ kind: "dashboard" | "report" | (string & {}); id: string; name: string | null }>;
  hidden_count: number;
};

/** Delete a dashboard-level metric. 409 while something still uses it; the
 *  `ApiError.detail` is then a `MetricInUseDetail`. */
export async function deleteDashboardMetric(
  dashboardId: string,
  metricId: string,
  /** The revision the caller read; the backend refuses (409) if it changed. */
  ifMatch?: string,
): Promise<void> {
  const query = ifMatch !== undefined ? `?if_match=${encodeURIComponent(ifMatch)}` : "";
  await apiNoContent(
    `/dashboards/${encodeURIComponent(dashboardId)}/metrics/${encodeURIComponent(metricId)}${query}`,
    { method: "DELETE" },
  );
}

/** Make a dashboard's metric available everywhere in the account. Account
 *  OWNER/ADMIN only (403 otherwise); 409 while it builds on a metric only
 *  this dashboard has. */
export async function promoteDashboardMetric(
  dashboardId: string,
  metricId: string,
  /** The revision the caller read; the backend refuses (409) if it changed. */
  ifMatch?: string,
): Promise<DashboardMetricDTO> {
  return apiJson<DashboardMetricDTO>(
    `/dashboards/${encodeURIComponent(dashboardId)}/metrics/${encodeURIComponent(metricId)}/promote`,
    ifMatch !== undefined
      ? { method: "POST", body: JSON.stringify({ if_match: ifMatch }) }
      : { method: "POST" },
  );
}
